import { CamelKeysToSnakeDeep } from '@core/classes/UtilClasses';

/** The exception that is thrown when a response from the Plaid API contains an error */
export interface PlaidError {
	/** A broad categorization of the error. */
	errorType: string;

	/** The particular error code. Safe for programmatic use. */
	errorCode: string;

	/** A developer-friendly representation of the error code. This may change over time and is not safe for programmatic use */
	errorMessage: string;

	/** A user-friendly representation of the error code. `null` if the error is not related to user action. This may change over time and is not safe for programmatic use. */
	displayMessage?: string;

	/** A unique string identifying the request, to be used for troubleshooting purposes. */
	requestId?: string;

	/** In the Assets product, a request can pertain to more than one Item.
	 * If an error is returned for such a request, causes will return an array of errors containing a breakdown of these errors on the individual Item level, if any can be identified.
	 */
	causes?: PlaidErrorCause[];

	/** The URL of a Plaid documentation page with more information about the error */
	documentationUrl?: string;

	/** Suggested steps for resolving the error */
	suggestedAction?: string;
}

export interface PlaidErrorCause extends PlaidError {
	/** The `itemId` of the Item associated with this webhook, warning, or error */
	itemId: string;
}

export function ConvertPlaidErrorToCamelCase(snakeObj: CamelKeysToSnakeDeep<PlaidError>): PlaidError {
	if (snakeObj == null) return null;

	const convertedError: PlaidError = {
		errorType: snakeObj.error_type,
		errorCode: snakeObj.error_code,
		errorMessage: snakeObj.error_message,
		displayMessage: snakeObj.display_message,
		requestId: snakeObj.request_id,
		causes: [],
		documentationUrl: snakeObj.documentation_url,
		suggestedAction: snakeObj.suggested_action,
	};

	snakeObj?.causes?.forEach(c => {
		const convertedCause = ConvertPlaidErrorToCamelCase(c) as PlaidErrorCause;
		convertedCause.itemId = c.item_id;
		convertedError.causes.push(convertedCause);
	});

	return convertedError;
}
